import React, { useState, useEffect, useCallback } from 'react';
import { Download, Search, RefreshCw, AlertCircle } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { adminGetLeads, adminUpdateLeadStatus, adminExportLeads } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

const STATUS_OPTIONS = ['NEW','CONTACTED','QUALIFIED','CLOSED','LOST'];
const STATUS_COLOR = { NEW:'#3b82f6', CONTACTED:'#f59e0b', QUALIFIED:'#10b981', CLOSED:'#6b7280', LOST:'#ef4444' };

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchLeads = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { page, size: 20 };
      if (filterStatus) params.status = filterStatus;
      const res = await adminGetLeads(params);
      const d = res.data.data;
      setLeads(d.content || []);
      setTotalPages(d.totalPages || 0);
      setTotal(d.totalElements || 0);
    } catch (err) {
      const msg = err.response?.status === 401 ? 'Session expired. Please log in again.' : 'Failed to load leads. Check server connection.';
      setError(msg);
    } finally { setLoading(false); }
  }, [page, filterStatus]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateLeadStatus(id, status);
      setLeads(prev => prev.map(l => l.id === id ? {...l, leadStatus:status} : l));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const exportCSV = async () => {
    try {
      const res = await adminExportLeads();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'leads-export.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.name?.toLowerCase().includes(q) || l.phone?.includes(search) || l.email?.toLowerCase().includes(q);
  });

  const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}) : '—';

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page__header">
          <div>
            <h1>Leads</h1>
            <p>{total} enquir{total===1?'y':'ies'} received</p>
          </div>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
            <button className="admin-btn-secondary" onClick={fetchLeads}><RefreshCw size={14}/> Refresh</button>
            <button className="export-btn" onClick={exportCSV}><Download size={14}/> Export CSV</button>
          </div>
        </div>

        {error && (
          <div style={{background:'#fff0f0',border:'1px solid rgba(234,46,43,0.2)',padding:'12px 16px',borderRadius:'var(--radius)',color:'var(--primary)',fontSize:'13px',display:'flex',alignItems:'center',gap:'8px',marginBottom:'16px'}}>
            <AlertCircle size={15}/> {error}
          </div>
        )}

        <div className="admin-card">
          <div className="filter-bar">
            <div className="filter-input-wrap">
              <Search size={14}/>
              <input placeholder="Search by name, phone or email..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <select value={filterStatus} onChange={e=>{ setFilterStatus(e.target.value); setPage(0); }}>
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="admin-loading"><div className="admin-spinner"/><p>Loading leads...</p></div>
          ) : filtered.length === 0 ? (
            <div className="admin-empty-state">
              <p>{search || filterStatus ? 'No leads match your filter.' : 'No leads yet. Enquiry form submissions will appear here.'}</p>
              {(search || filterStatus) && <button className="link-btn" onClick={()=>{setSearch('');setFilterStatus('');}}>Clear filters</button>}
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr><th>Contact</th><th>Property</th><th>Message</th><th>Status</th><th>Received</th></tr>
              </thead>
              <tbody>
                {filtered.map(l => (
                  <tr key={l.id}>
                    <td style={{minWidth:'140px'}}>
                      <strong style={{display:'block',fontSize:'14px'}}>{l.name}</strong>
                      <span className="lead-detail-cell">{l.phone}</span>
                      {l.email && <span className="lead-detail-cell">{l.email}</span>}
                    </td>
                    <td className="td-truncate" style={{color:'var(--text-2)',fontSize:'13px'}}>{l.propertyTitle || <span style={{color:'var(--text-3)',fontStyle:'italic'}}>General Enquiry</span>}</td>
                    <td style={{maxWidth:'220px',fontSize:'13px',color:'var(--text-2)',lineHeight:1.5}}>{l.message || '—'}</td>
                    <td style={{minWidth:'120px'}}>
                      <select
                        value={l.leadStatus}
                        onChange={e => updateStatus(l.id, e.target.value)}
                        className="status-select"
                        style={{background:STATUS_COLOR[l.leadStatus]+'14',color:STATUS_COLOR[l.leadStatus],borderColor:STATUS_COLOR[l.leadStatus]+'40'}}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{fontSize:'12px',color:'var(--text-3)',whiteSpace:'nowrap'}}>{fmt(l.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button disabled={page===0} onClick={()=>setPage(p=>p-1)} className="page-btn">← Prev</button>
              <span>Page {page+1} of {totalPages}</span>
              <button disabled={page>=totalPages-1} onClick={()=>setPage(p=>p+1)} className="page-btn">Next →</button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;
